import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon, Check as CheckIcon } from "@mui/icons-material";
import { useNavigation } from "contexts/notificationsContext";
import { listarCodigosBase, listarGruposCodigosBase, ICodigoBase, IGrupoCodigoBase } from "services/modules/aplicabilidades";
import { StripedDataGrid } from "components/StripedDataGrid";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";

interface ModalAgrupamentoBasesProps {
  open: boolean;
  onClose: () => void;
  idEmpresa: number;
  idComponenteBaseAtual: number; // Código base atual (para excluir da lista)
  onSave: (codigosBaseSelecionados: ICodigoBase[]) => void | Promise<void>;
}

export const ModalAgrupamentoBases: React.FC<ModalAgrupamentoBasesProps> = ({
  open,
  onClose,
  idEmpresa,
  idComponenteBaseAtual,
  onSave,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [codigosBase, setCodigosBase] = useState<ICodigoBase[]>([]);
  const [codigosSelecionados, setCodigosSelecionados] = useState<Map<number, ICodigoBase>>(new Map());
  const [filtroBusca, setFiltroBusca] = useState("");
  // FASE 03 (item 5): filtro de Grupo agora é o IdGrupo (numérico) → filtro server-side via
  // listarCodigosBase({ idGrupo }). Antes era a descrição (string), que não filtrava de fato.
  const [filtroGrupo, setFiltroGrupo] = useState<number | "">("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  // FASE 03 (item 5): grupos carregados uma única vez do backend (todos os códigos base elegíveis),
  // não derivados apenas da página atual do grid (que agora é server-side).
  const [gruposDisponiveis, setGruposDisponiveis] = useState<IGrupoCodigoBase[]>([]);

  // Debounced search
  const debouncedFiltroBusca = useCallback(
    debounce((value: string) => {
      setFiltroBusca(value);
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  useEffect(() => {
    if (open) {
      setFiltroBusca("");
      setFiltroGrupo("");
      setCodigosSelecionados(new Map());
      setPage(1);
      // FASE 03 (item 5): limpa a lista anterior ao (re)abrir — evita exibir os registros do
      // filtro anterior enquanto a nova busca não chega (sintoma relatado pelo QA).
      setCodigosBase([]);
      setTotalRecords(0);
    }
  }, [open]);

  // FASE 03 (item 5): carrega os grupos uma única vez ao abrir o modal (não a cada página/busca).
  useEffect(() => {
    if (!open) return;
    let ativo = true;
    (async () => {
      try {
        const grupos = await listarGruposCodigosBase(idEmpresa);
        if (ativo) setGruposDisponiveis(grupos);
      } catch (error) {
        console.error("Erro ao carregar grupos de códigos base:", error);
        if (ativo) setGruposDisponiveis([]);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [open, idEmpresa]);

  useEffect(() => {
    if (open) {
      carregarCodigosBase();
    }
  }, [open, filtroBusca, filtroGrupo, page, pageSize, idEmpresa]);

  const carregarCodigosBase = async () => {
    setIsLoading(true);
    // FASE 03 (item 5): limpa a lista antes de cada busca para não exibir o resultado anterior.
    setCodigosBase([]);
    try {
      // FASE 03 (item 5): paginação SERVER-SIDE (page/limit/filtroBusca/idGrupo) — substitui o
      // antigo limit:1000 com paginação no cliente, que deixava a abertura/filtragem lenta.
      const response = await listarCodigosBase({
        idEmpresa,
        page,
        limit: pageSize,
        filtroBusca: filtroBusca || undefined,
        idGrupo: filtroGrupo ? Number(filtroGrupo) : undefined,
      });

      const items = (response.data?.items || []) as ICodigoBase[];
      const total = response.data?.totalRecords ?? items.length;

      // Filtrar o código base atual (não deve aparecer na lista). Como é só 1 código, eventual
      // remoção dentro da página corrente é cosmética e não compromete a paginação.
      const codigos = items.filter((c: ICodigoBase) => c.idComponenteBase !== idComponenteBaseAtual);

      setCodigosBase(codigos);
      setTotalRecords(total);
    } catch (error) {
      console.error("Erro ao carregar códigos base:", error);
      openNotification({ message: "Erro ao carregar códigos base", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // FASE 03 (item 5): guarda o OBJETO selecionado (não só o id). Com a paginação server-side,
  // `codigosBase` contém só a página atual; reter os objetos preserva a seleção entre páginas.
  const handleToggleSelecao = (codigo: ICodigoBase) => {
    setCodigosSelecionados((prev) => {
      const novo = new Map(prev);
      if (novo.has(codigo.idComponenteBase)) {
        novo.delete(codigo.idComponenteBase);
      } else {
        novo.set(codigo.idComponenteBase, codigo);
      }
      return novo;
    });
  };

  const handleSave = async () => {
    if (codigosSelecionados.size < 1) {
      openNotification({ message: "Selecione pelo menos um código base", type: "error" });
      return;
    }

    const codigosSelecionadosArray = Array.from(codigosSelecionados.values());
    await onSave(codigosSelecionadosArray);
    onClose();
  };

  // Função para construir URL da imagem
  const buildImageSrc = (icone?: string | null): string => {
    if (!icone) return "";
    const trimmed = icone.trim();
    if (!trimmed) return "";
    
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    
    if (trimmed.startsWith("/")) {
      return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}${trimmed}`;
    }
    
    return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}/${trimmed}`;
  };

  // FASE 03 (item 5): opções do dropdown Grupo a partir da lista completa carregada do backend
  // (value = IdGrupo numérico, para filtro server-side).
  const gruposOpcoes = useMemo(
    () => [
      { value: "" as number | "", label: "Todos" },
      ...gruposDisponiveis.map((g) => ({ value: g.idGrupo as number | "", label: g.descricao })),
    ],
    [gruposDisponiveis]
  );

  const columns: GridColDef[] = [
    {
      field: "imagem",
      headerName: "",
      width: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const imageUrl = buildImageSrc(params.row.icone);
        return (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={params.row.codigoBase}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "codigoBase",
      headerName: "Código",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "cor",
      headerName: "Cor",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "grupo",
      headerName: "Grupo",
      width: 150,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const isSelected = codigosSelecionados.has(params.row.idComponenteBase);
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              // Evita double-toggle: sem isso, o clique no "Ações" também dispara onRowClick.
              e.stopPropagation();
              handleToggleSelecao(params.row as ICodigoBase);
            }}
            sx={{
              color: isSelected ? "#1976d2" : "#9e9e9e",
            }}
          >
            <CheckIcon sx={{ fontSize: 22 }} />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent dividers sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Título da seção */}
          <Typography 
            variant="body1" 
            sx={{ 
              color: "#666", 
              pb: 2, 
              borderBottom: "1px solid #e0e0e0",
              mb: 1 
            }}
          >
            Selecionar Códigos Base para Agrupamento
          </Typography>

          {/* Contador de seleção */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {codigosSelecionados.size} código(s) base selecionado(s)
            </Typography>
          </Box>

          {/* Filtros */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <Box sx={{ minWidth: 200, maxWidth: 300 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "#4db6ac", 
                  fontWeight: 500,
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.75rem"
                }}
              >
                Grupo
              </Typography>
              <FormControl fullWidth variant="outlined">
                <MuiSelect
                  value={filtroGrupo === "" ? "" : filtroGrupo}
                  displayEmpty
                  onChange={(e) => {
                    const v = e.target.value;
                    setFiltroGrupo(v === "" ? "" : Number(v));
                    setPage(1);
                  }}
                  sx={{
                    bgcolor: "#f5f5f5",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0",
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: "#999" }}>Selecione</Typography>;
                    }
                    return gruposOpcoes.find(g => g.value === selected)?.label || "";
                  }}
                >
                  {gruposOpcoes.map((grupo) => (
                    <MenuItem key={grupo.value} value={grupo.value}>
                      {grupo.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Box>

            <TextField
              placeholder="Buscar por código ou descrição"
              fullWidth
              variant="outlined"
              onChange={(e) => debouncedFiltroBusca(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f5f5f5",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#b0b0b0",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabela */}
          <Box sx={{ height: 400, width: "100%" }}>
            <StripedDataGrid
              columns={columns}
              rows={codigosBase.map((row) => ({
                ...row,
                isSelected: codigosSelecionados.has(row.idComponenteBase),
              }))}
              getRowId={(row) => row.idComponenteBase}
              loading={isLoading}
              paginationMode="server"
              page={page - 1}
              pageSize={pageSize}
              rowCount={totalRecords}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => {
                setPageSize(newPageSize);
                setPage(1);
              }}
              checkboxSelection={false}
              disableSelectionOnClick
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  "&.highlighted": {
                    backgroundColor: "rgba(33, 150, 243, 0.12) !important",
                  },
                },
              }}
              getRowClassName={(params) => {
                return params.row.isSelected ? "highlighted" : "";
              }}
              onRowClick={(params) => {
                handleToggleSelecao(params.row as ICodigoBase);
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading} variant="outlined">
          CANCELAR
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={isLoading || codigosSelecionados.size < 1}
        >
          CONTINUAR ({codigosSelecionados.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

